import React from "react";

const HeroSection = ({ onScrollToTool }) => {
  return (
    <>
      {/* Section 1: Hero with Background Image */}
      <section className="relative text-white overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/assets/IT_Procurement_PARE_BANAR.png')",
          }}
        >
          {/* Dark Overlay for better text readability */}
          {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/85 to-blue-700/90" /> */}
        </div>
        
        {/* Background decoration with enhanced blur */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-10 w-96 h-96 rounded-full bg-white blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-blue-300 blur-2xl animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-cyan-400 blur-3xl opacity-30" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            {/* Badge - Glass morphism */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-4 py-1.5 mb-6 shadow-lg">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm font-medium text-white">
                Real-Time Market Benchmarking
              </span>
            </div>

            <h1 className="bg-blue-300 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 bg-clip-text text-transparent">
              Smart IT Procurement
              <span className="block text-blue-300">at Better-Than-Market Prices</span>
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed max-w-2xl backdrop-blur-sm bg-white/5 p-4 rounded-2xl inline-block">
             For Startups, Agencies, Schools, Colleges & Corporates — Bulk Deals. Genuine Products. Fast Delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onScrollToTool}
                className="inline-flex items-center justify-center gap-2 bg-white/95 backdrop-blur-md text-blue-800 font-semibold px-8 py-4 rounded-xl hover:bg-white transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 border border-white/50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Get Instant Price
              </button>
              <button
                onClick={onScrollToTool}
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/20 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Quotation
              </button>
            </div>
          </div>

          {/* Stats row - Glass morphism cards */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 pt-10">
            {[
              { label: "Lower your cost per unit instantly", value: "Bulk Buying = Bigger Savings" },
              { label: "Reliable shipping via DTDC", value: "Fast 48H Delivery Across India" },
              { label: "Always benchmarked against live market rates", value: "Better-Than-Market Pricing" },
              { label: "Professional, ready-to-send quotations", value: "Get Quotes in Seconds" },
            ].map((stat) => (
              <div 
                key={stat.label} 
                className="text-center bg-white/10 backdrop-blur-md rounded-2xl p-4 md:p-6 border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Our Origin Story - Pan India IT Hardware Supply */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 rounded-full bg-cyan-400 blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-blue-300 blur-2xl animate-pulse" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-4 py-1.5 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm font-medium text-white">Our Origin Story</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Pan India IT Hardware Supply
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* Left Column - Story Content */}
            <div className="space-y-6">
              {/* Main Story Card */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 border border-white/30 shadow-2xl hover:bg-white/15 transition-all duration-500">
                <div className="flex items-center gap-3 mb-5">
                  <div className="p-2 bg-blue-500/30 rounded-xl">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">
                    Unlock Better-Than-Market IT Pricing
                  </h3>
                </div>
                <div className="space-y-4 text-white/90 text-sm md:text-base leading-relaxed">
                  <p>
                    Watch how a simple Amazon product link turns into your business price—powered by direct sourcing.
                  </p>
                  <div className="bg-white/5 rounded-xl p-4 border-l-4 border-amber-400">
                    <p className="text-xs text-white/70 italic">
                      ⚠️ <strong>Disclaimer:</strong> We use Amazon listings for reference and comparison only. Orator Hype is not affiliated with Amazon or its sellers.
                    </p>
                  </div>
                  <p>
                    By eliminating platform fees, seller margins, and heavy advertising costs, we are able to deliver significantly better-than-market pricing — often among the most competitive options available for business buyers in India.
                  </p>
                  <div className="bg-white/5 rounded-xl p-4 border-l-4 border-blue-400">
                    <p className="text-xs text-white/70 italic">
                      ℹ️ All pricing and claims are presented in accordance with applicable Indian consumer protection and fair advertising guidelines.
                    </p>
                    <p className="text-xs text-white/80 font-medium mt-2">
                      Orator Hype PVT LTD — Smarter Pricing. Better Business.
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Highlights */}
              {/* <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🏢</div>
                  <p className="text-xs font-medium">Corporate</p>
                  <p className="text-xs text-white/70">Volume Deals</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🎓</div>
                  <p className="text-xs font-medium">Educational</p>
                  <p className="text-xs text-white/70">Special Pricing</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">🚀</div>
                  <p className="text-xs font-medium">Startups</p>
                  <p className="text-xs text-white/70">Growth Support</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">📦</div>
                  <p className="text-xs font-medium">Pan India</p>
                  <p className="text-xs text-white/70">Fast Shipping</p>
                </div>
              </div> */}
            </div>

            {/* Right Column - Video Section */}
            <div className="relative">
              {/* Video Card */}
              <div className="rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-white/30 bg-white/5 backdrop-blur-sm transform hover:scale-[1.02] transition-transform duration-500">
                {/* Video Container */}
                <div className="relative">
                  <video
                    className="w-full h-auto md:h-[380px] lg:h-[420px] object-cover"
                    autoPlay
                    loop
                    playsInline
                    controls
                    controlsList="nodownload"
                  >
                    <source src="/assets/ORATOT_IT_DEMO VIDEO.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Gradient Overlay for better aesthetics */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
                </div>
                
                {/* Video Caption */}
                <div className="p-4 bg-white/5 border-t border-white/20">
                  <p className="text-center text-sm text-white/80 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    See how it works — Direct sourcing in action
                  </p>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-bounce">
                Watch Demo →
              </div>
            </div>
          </div>
          
          {/* Bottom Note */}
          
        </div>
      </section>
    </>
  );
};

export default HeroSection;